import {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import type {AnyAction} from "redux";
import type {ThunkDispatch} from "redux-thunk";
import {DETAILS_VIEW_TYPE, LOADING_STATUS} from "utils/constants";
import {handleNPKeyNavigation} from "utils/keyPressHandler.function";
import * as reduxSelectors from "components/store/application.reducers";
import {taskAction} from "components/mytasks/action/taskAction.action";
import {navigateBetweenTasks} from "components/mytasks/action/navigateBetweenTasks.action";
import {
    removeSelectedDocument, handleAddTasksDraftComments, getTaskCommentsByUid, getTaskDetailsByUID,
} from "components/taskdetails-remake/taskDetails.action";
import {PATH_TO_MY_TASKS} from "components/router/paths.js";
import {Loader, LoaderSize} from "components/common/Loader.component";
import {PanelErrorFallback} from "components/errorpage/PanelErrorBoundary";
import TaskDetails from "./TaskDetails.component";
import {getActionsForTask, getTaskNavigation} from "./taskDetailsMasterLayer.function";
import type {TaskDetailsMasterLayerProps} from "types/taskDetailsMasterLayer";

/**
 * The task details page of /tasks/:uid, behind fe-task-details-redesign. The task is read from the
 * details once they arrive, and from the My tasks list until then.
 */
export const TaskDetailsMasterLayerByUID = (props: TaskDetailsMasterLayerProps) => {
    const tasks = useSelector(reduxSelectors.getTasksForRender);
    const activeTask = useSelector(reduxSelectors.getActiveTask);
    const redirectedTask = useSelector(reduxSelectors.getRedirectedTask);
    const taskDetails = useSelector(reduxSelectors.getDetails);
    const oneHandleOnly = useSelector(reduxSelectors.getOneHandleOnlyFlag);
    const attachments = useSelector(reduxSelectors.getTaskAttachments);
    const loadingStatus = useSelector(reduxSelectors.getAttachmentsLoadingStatus);
    const tasksWithPayment = useSelector(reduxSelectors.getTasksWithPayment);
    // typed for thunks, so navigateBetweenTasks hands back the path it computes
    const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
    const keyPressRef = useRef<(e: KeyboardEvent) => void>(() => {});

    const routeUid = props.match.params.uid;
    // until the details arrive, fall back to the list entry as the key route does, so moving between tasks keeps the page mounted
    const task = activeTask?.uid === routeUid
        ? activeTask
        : (tasks.allTasks || []).find((x: any) => x.task.uid === routeUid)?.task;

    useEffect(() => {
        dispatch(removeSelectedDocument());
        // the page stays mounted between tasks, so the listener must read the latest uid, not the one it was added with
        const onKeyDown = (e: KeyboardEvent) => keyPressRef.current(e);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- set up once on mount
    }, []);

    // this also runs on mount, so it is the only place the details are requested
    useEffect(() => {
        try {
            dispatch(getTaskDetailsByUID(routeUid));
        } catch (e) {
            props.history("/");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- requested again only for another task
    }, [routeUid]);

    useEffect(() => {
        if (redirectedTask) {
            props.history("/processes/" + redirectedTask.uidProcess);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the task turns out to be a process
    }, [redirectedTask]);

    const taskNavigation = (uid: string, isForward: boolean) => {
        props.history(dispatch(navigateBetweenTasks(uid, isForward)));
    };

    keyPressRef.current = (e: KeyboardEvent) => {
        handleNPKeyNavigation(e, routeUid, taskNavigation);
    };

    // the action buttons and the keyboard pass the navigation to do afterwards in options.navigate
    const dispatchTaskAction = (type: any, taskIds = [], options = {}) => {
        dispatch(taskAction(type, taskIds, options));
    };

    const dispatchHandleAddTasksDraftComments = (taskKey: string, comment: string, documentUid: string, uid: string) => {
        dispatch(handleAddTasksDraftComments(taskKey, comment, documentUid, uid));
    };

    // the panel passes (uid, taskId, skipLoading), the task comments endpoint only needs the uid
    const dispatchGetTaskCommentsByUid = (uid: string, _taskId: string, skipLoading = false) => {
        dispatch(getTaskCommentsByUid(uid, skipLoading));
    };

    // a failed load leaves no task to render, so the spinner below would never resolve (AC-12773)
    if (loadingStatus === LOADING_STATUS.ERROR)
        return <PanelErrorFallback/>;

    // the store may still hold the previously opened task, so rendering it would fire the panel requests with its uid
    if (task?.uid !== routeUid) {
        return <div className="position-fixed top-50 w-100">
            <Loader size={LoaderSize.Large}/>
        </div>;
    }

    const navigation = getTaskNavigation(task, tasks, oneHandleOnly);
    const activeActions = getActionsForTask(task, navigation, taskDetails, tasksWithPayment);

    return (
        <TaskDetails params={{taskId: task.key, uid: task.uid}}
                     task={task}
                     taskAction={dispatchTaskAction}
                     navigation={navigation}
                     attachments={attachments}
                     taskDetails={taskDetails}
                     loadingStatus={loadingStatus}
                     route={props.route}
                     activeActions={activeActions}
                     navigateBetweenTasks={taskNavigation}
                     navigateBackPath={PATH_TO_MY_TASKS}
                     navigateBack={() => {
                     }}
                     type={DETAILS_VIEW_TYPE.TASK}
                     routerBlocker={props.blocker}
                     allowActionlessComments={taskDetails?.taskPermission?.allowExtraComments}
                     addCommentHandler={dispatchHandleAddTasksDraftComments}
                     getCommentsHandler={dispatchGetTaskCommentsByUid}/>
    );
};

export default TaskDetailsMasterLayerByUID;
